import React from 'react';
import { Link } from 'react-router-dom';
import './MatchDetails.css';

/**
 * MatchDetails component
 * Displays game-by-game match results
 * T067: Match details with game results for public viewing
 */
const MatchDetails = ({ match, games = [], team1, team2 }) => {
  if (!match) {
    return (
      <div className="match-details-empty">
        <p>Match details not available.</p>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBD';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getWinnerName = (game, playerMap) => {
    if (!game.winnerId) return 'N/A';
    const player = playerMap[game.winnerId];
    return player ? player.name : 'Unknown';
  };

  // Create a map of player IDs to player names from team rosters
  const playerMap = {};
  if (team1?.players) {
    team1.players.forEach((player) => {
      playerMap[player.playerId] = player;
    });
  }
  if (team2?.players) {
    team2.players.forEach((player) => {
      playerMap[player.playerId] = player;
    });
  }

  return (
    <div className="match-details">
      <div className="match-header">
        <div className="match-teams">
          <div className="team-info">
            <Link to={`/teams/${match.team1Id}`} className="team-name">
              {team1?.name || 'Team 1'}
            </Link>
            <span className="team-score">{match.team1Score ?? 0}</span>
          </div>

          <span className="match-vs">vs</span>

          <div className="team-info">
            <Link to={`/teams/${match.team2Id}`} className="team-name">
              {team2?.name || 'Team 2'}
            </Link>
            <span className="team-score">{match.team2Score ?? 0}</span>
          </div>
        </div>

        <div className="match-meta">
          <span className="match-date">{formatDate(match.matchDate)}</span>
          {match.venue && (
            <span className="match-venue">📍 {match.venue}</span>
          )}
        </div>
      </div>

      {games.length > 0 && (
        <div className="games-section">
          <h3>Game-by-Game Results</h3>
          <div className="games-list">
            {games.map((game) => {
              const team1Player = playerMap[game.team1PlayerId];
              const team2Player = playerMap[game.team2PlayerId];
              const isTeam1Winner = game.winnerId === game.team1PlayerId;

              return (
                <div key={game.gameId} className="game-item">
                  <div className="game-number">Game {game.gameNumber}</div>

                  <div className="game-matchup">
                    <div className={`player ${isTeam1Winner ? 'winner' : ''}`}>
                      <span className="player-name">
                        {team1Player?.name || 'Unknown'}
                      </span>
                      {game.breaker === game.team1PlayerId && (
                        <span className="breaker-badge" title="Breaking">
                          🎱
                        </span>
                      )}
                      {isTeam1Winner && (
                        <span className="winner-badge">✓</span>
                      )}
                    </div>

                    <span className="vs">vs</span>

                    <div className={`player ${!isTeam1Winner ? 'winner' : ''}`}>
                      <span className="player-name">
                        {team2Player?.name || 'Unknown'}
                      </span>
                      {game.breaker === game.team2PlayerId && (
                        <span className="breaker-badge" title="Breaking">
                          🎱
                        </span>
                      )}
                      {!isTeam1Winner && game.winnerId === game.team2PlayerId && (
                        <span className="winner-badge">✓</span>
                      )}
                    </div>
                  </div>

                  {game.lagWinner && (
                    <div className="game-detail">
                      <span className="detail-label">Lag:</span>
                      <span className="detail-value">
                        {playerMap[game.lagWinner]?.name || 'Unknown'}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {games.length === 0 && (
        <div className="games-empty">
          <p>No game results recorded yet.</p>
        </div>
      )}
    </div>
  );
};

export default MatchDetails;
